import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Billables() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [funding, setFunding] = useState("all");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status_filter = status;
    if (funding !== "all") params.funding_source = funding;
    return api.get("/billables", { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [status, funding]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/billables/summary").then((r) => setSummary(r.data)); }, [rows.length]);

  const total = rows.reduce((s, r) => s + (r.total_amount || 0), 0);
  const unbilled = rows.filter((r) => r.status === "unbilled").reduce((s, r) => s + (r.total_amount || 0), 0);
  const paid = rows.filter((r) => r.status === "paid").reduce((s, r) => s + (r.total_amount || 0), 0);

  return (
    <div className="fade-in" data-testid="billables-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Billing</div>
          <h2 className="font-serif text-3xl text-njas-navy">Billables</h2>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2 px-3" data-testid="billables-status-filter">
            <option value="all">All statuses</option><option value="unbilled">Unbilled</option><option value="invoiced">Invoiced</option><option value="paid">Paid</option><option value="written_off">Written off</option>
          </select>
          <select value={funding} onChange={(e) => setFunding(e.target.value)} className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2 px-3">
            <option value="all">All funding</option><option value="medicare">Medicare</option><option value="ndis">NDIS</option><option value="private">Private</option><option value="dva">DVA</option><option value="pro_bono">Pro bono</option><option value="other">Other</option>
          </select>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Filtered total" value={`$${total.toFixed(2)}`} />
        <StatCard label="Unbilled" value={`$${unbilled.toFixed(2)}`} accent="text-amber-700" />
        <StatCard label="Paid" value={`$${paid.toFixed(2)}`} accent="text-emerald-700" />
      </div>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Client</th><th className="px-4 py-2 text-left">Worker</th><th className="px-4 py-2 text-left">MBS</th><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-right">Duration</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-left">Funding</th><th className="px-4 py-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-4 py-8 text-center text-njas-muted">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-njas-muted">No billables. Add them from a client&apos;s Billables tab.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--njas-border)]" data-testid={`billable-${r.id}`}>
                  <td className="px-4 py-2">{r.service_date ? new Date(r.service_date).toLocaleDateString("en-AU") : "—"}</td>
                  <td className="px-4 py-2"><Link to={`/dashboard/clients/${r.client_id}`} className="text-njas-navy hover:underline">{r.client_name}</Link></td>
                  <td className="px-4 py-2 text-njas-muted">{r.worker_name}</td>
                  <td className="px-4 py-2 text-njas-muted">{r.mbs_item || "—"}</td>
                  <td className="px-4 py-2">{r.service_description}</td>
                  <td className="px-4 py-2 text-right">{r.duration_minutes} min</td>
                  <td className="px-4 py-2 text-right">${(r.total_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 capitalize">{r.funding_source}</td>
                  <td className="px-4 py-2 capitalize">{r.status?.replace("_", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5">
      <div className="text-xs text-njas-muted uppercase tracking-wider">{label}</div>
      <div className={`font-serif text-3xl mt-1 ${accent || "text-njas-navy"}`}>{value}</div>
    </div>
  );
}
